import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {PublicationModel, PublicationSaveModel} from '../../../../models/publication.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {LanguageModel} from '../../../../models/language.model';
import {CodebookModel} from '../../../../models/codebook.model';
import {AppCommunicationService} from '../../../../services/app-communication.service';
import {BindingTypeCodebook, BindingTypeEnum} from '../../../../codebook/binding-type-codebook';
import {DocumentTypeCodebook} from '../../../../codebook/document-type-codebook';
import {ThematicGroupTypeCodebook} from '../../../../codebook/thematic-group-type-codebook';
import {LanguagesCodebook} from '../../../../codebook/languages-codebook';
import {I18nService} from '../../../../../shared/i18n';
import {EditionTypeCodes} from '../../../../constants';
import {GeneralService} from '../../../../services/general.service';

@Component({
    selector: 'app-publication-information-form-part',
    templateUrl: './publication-information-form-part.component.html',
    styleUrls: ['./publication-information-form-part.component.scss']
})

export class PublicationInformationFormPartComponent implements OnInit {
    documentTypes: CodebookModel[] = [];
    thematicGroupeTypes: CodebookModel[] = [];
    bindingTypes: CodebookModel[] = [];
    validityLabel = false;

    editionTypes = EditionTypeCodes;

    @Output('onValidChange')
    onValidChange = new EventEmitter<boolean>();

    @Output('onModelUpdate')
    onModelUpdate = new EventEmitter<PublicationSaveModel>();

    @ViewChild('inputEdition')
    inputEdition;

    @ViewChild('selectEdition')
    selectEdition;

    publicationFormPart: FormGroup;
    allLanguages: LanguageModel[] = [];
    languageSuggestions: LanguageModel[] = [];
    minDate = new Date();
    example: any[] = [];

    constructor(private formBuilder: FormBuilder
        , private _appCommunicationService: AppCommunicationService
        , private i18nService: I18nService,
                private generalService: GeneralService
    ) {
        this.initFormPart();
        this.documentTypes = DocumentTypeCodebook.allTypes;
        this.bindingTypes = BindingTypeCodebook.allTypes;
        this.thematicGroupeTypes = ThematicGroupTypeCodebook.allTypes;
        this.allLanguages = LanguagesCodebook.allLanguages;

        this.initCodebook();
    }

    initCodebook() {
        this.generalService
            .getDocumentCodebook()
            .subscribe(v => {
                this.documentTypes = v;
            })
    }

    ngOnInit() {
        this.allLanguages.forEach(l => l.name = this.i18nService.getTranslation(l.name));
        this.i18nService.subscribe(() => this.allLanguages.forEach(l => l.name = this.i18nService.getTranslation(l.name)), (err) => console.error(err))
    }

    initFormPart() {
        this.publicationFormPart = this.formBuilder.group({
            title: ['', Validators.required],
            subtitle: [''],
            pages: ['', Validators.compose([Validators.required, Validators.min(0), Validators.max(32767)])],
            year: ['' + new Date().getFullYear(), [Validators.required, Validators.min(1990), Validators.max(2085)]],
            edition: ['', Validators.required],
            printCount: ['', [Validators.required, Validators.min(0), Validators.max(32767)]],
            documentType: ['', Validators.required],
            bindingType: ['', Validators.required],
            thematicGroupType: ['', Validators.required],
            estimatedPrintDeliveryDate: ['', Validators.required],
            languages: [[], Validators.required],
            link: [''],
            comment: [],
        });
        this.publicationFormPart.valueChanges
            .subscribe(() => {
                this.checkFormValidation();
                this.onModelUpdate.emit(this.publicationFormPart.value)
            })
    }

    search(event) {
        const query = event.query.toLowerCase();
        this.languageSuggestions = this.allLanguages.filter(lang =>
            lang.name.toLowerCase().startsWith(query)
            || lang.shortcut.toLowerCase().startsWith(query)
        );
    }

    checkFormValidation() {
        let isValid = false;

        if (this.publicationFormPart.get('bindingType').value.id === BindingTypeEnum.online) {
            this.publicationFormPart.get('printCount').disable({onlySelf: true, emitEvent: false});
            this.publicationFormPart.get('printCount').clearValidators();
            this.publicationFormPart.get('printCount').patchValue(0, {onlySelf: true, emitEvent: false});
        } else {
            this.publicationFormPart.get('printCount').enable({onlySelf: true, emitEvent: false});
            this.publicationFormPart.get('printCount').setValidators([Validators.min(1)]);
        }

        if (this.publicationFormPart.valid) {

            if (this.publicationFormPart.get('bindingType').value.id === BindingTypeEnum.online) {

                if (this.publicationFormPart.get('link').value && this.publicationFormPart.get('link').value.length > 0) {
                    isValid = true;
                }

            } else {
                isValid = true;
            }

        }

        if (isValid) {
            this.validityLabel = false;
            this.onValidChange.emit(true);
        } else {
            this.onValidChange.emit(false);
        }

        return isValid;
    }

    onSubmit(): PublicationModel {
        return this.publicationFormPart.value;
    }

    setFormValue(publication: PublicationModel) {
        publication.documentType = this.documentTypes.filter(type => type.id === publication.documentTypeId)[0];
        publication.bindingType = this.bindingTypes.filter(type => type.id === publication.bindingTypeId)[0];
        publication.thematicGroupType = this.thematicGroupeTypes.filter(type => type.id === publication.thematicGroupTypeId)[0];
        publication.languages = this.allLanguages.filter(type => (publication.languages.some(l => l.id === type.id)));
        publication.estimatedPrintDeliveryDate = new Date(publication.estimatedPrintDeliveryDate);
        this.publicationFormPart.setValue({
            title: publication.title,
            subtitle: publication.subtitle,
            pages: publication.pages,
            year: publication.year,
            edition: publication.edition,
            printCount: publication.printCount,
            documentType: publication.documentType,
            bindingType: publication.bindingType,
            thematicGroupType: publication.thematicGroupType,
            link: publication.link,
            comment: publication.comment == null ? '' : publication.comment,
            estimatedPrintDeliveryDate: publication.estimatedPrintDeliveryDate,
            languages: publication.languages
        });
        this._appCommunicationService.loadingOff();
        if (publication.isReminderSent === true) {
            this.publicationFormPart.get('estimatedPrintDeliveryDate').setValue('');
        }
        if ((publication.edition !== 'prvé') && (publication.edition !== 'druhé') && (publication.edition !== 'druhe prepracované')) {
            this.toggleField(this.selectEdition.nativeElement, this.inputEdition.nativeElement);
            this.selectEdition.nativeElement.selectedIndex = '0';
            this.publicationFormPart.get('edition').setValue(publication.edition);
            this.inputEdition.nativeElement.value = publication.edition;
        }
        this.publicationFormPart.controls['comment'].disable();
    }

    touchForm() {
        const controls = this.publicationFormPart.controls;
        if (this.publicationFormPart.invalid) {
            this.validityLabel = true;
        }
        Object.keys(controls).forEach(controlName => controls[controlName].markAsTouched());
    }

    toggleField(hideObj, showObj) {
        hideObj.disabled = true;
        hideObj.style.display = 'none';
        showObj.disabled = false;
        showObj.style.display = 'inline';
        showObj.focus();
    }

    onEditionSelectUpdate(selectEdition, inputEdition) {

        if (selectEdition.options[selectEdition.selectedIndex].value === 'customOption') {
            this.toggleField(selectEdition, inputEdition);
            selectEdition.selectedIndex = '0';
            this.publicationFormPart.get('edition').setValue('');
        }

    }

    onEditionInputBlur(selectEdition, inputEdition) {
        if (inputEdition.value === '') {
            this.toggleField(inputEdition, selectEdition);
            this.publicationFormPart.get('edition').setValue('');
        } else {
            this.publicationFormPart.get('edition').setValue(inputEdition.value);
        }
    }

    onEditionInputChange(inputEdition) {
        this.publicationFormPart.get('edition').setValue(inputEdition.value);

    }

    getYearRangeValue() {
        // return 2018:new Date().getFullYear() but 2 months before the new year add next year
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // Months are zero-based

        if (currentMonth >= 11) {
            return `${2018}:${currentYear + 1}`;
        } else {
            return `2018:${currentYear}`;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/+app-custom/components/publication-registration/base-publication-form-part/publication-information-form-part/publication-information-form-part.component.ts